import { Injectable } from '@angular/core';
import { Club } from '@shared/models/club';

import { APIService } from './api.service';
import { StateService } from './state.service';
import { Functionary } from './functionary';
import { BaseImage } from '@shared/models/image';
import { ImageService } from '@utils/services/image/image.service';
import { HttpClient } from '@angular/common/http';
import {TeamService} from '@core/services/team.service';
import {AppTeam} from '@shared/models/team';

export interface FunctionariesSortedByGroup {
  group: string;
  functionaries: Functionary[];
}

@Injectable({
  providedIn: 'root',
})
export class ClubService {

  private clubsRoute = 'clubs/';

  constructor(
    private http: HttpClient,
    private apiService: APIService,
    private appStateService: StateService,
    private imageService: ImageService,
    private teamService: TeamService,
  ) {}

  /***************************** HTTP GET FUNCTIONS *****************************/

  fetchClub(id: string) {
    return this.http.get(this.getClubRoute(id), this.apiService.getJsonOptions())
        .toPromise()
        .then(response => this.resolveClub(response))
        .catch(error => {
          return this.handleError(error);
        });
  }

  fetchClubOfficials(id: string): Promise<Functionary[]> {
    return this.http.get(this.getClubOfficialsRoute(id), this.apiService.getJsonOptions())
      .toPromise()
      .then(response => this.resolveOfficials(response))
      .catch(error => {
        return this.handleError(error);
      });
  }

  fetchClubOfficialsAndOrderByGroup(id: string): Promise<FunctionariesSortedByGroup[]> {
    return this.fetchClubOfficials(id).then(officials => {
      const sortedByGroupArray: FunctionariesSortedByGroup[] = [];

      const functionariesWithoutGroup: FunctionariesSortedByGroup = {
        group: null,
        functionaries: []
      };

      officials.forEach(official => {
        if (official.group == null) {
          functionariesWithoutGroup.functionaries.push(official);
        } else {
          const indexOfExistingGroup = sortedByGroupArray.findIndex(value => value.group === official.group);
          if (indexOfExistingGroup !== -1) {
            sortedByGroupArray[indexOfExistingGroup].functionaries.push(official);
          } else {
            sortedByGroupArray.push({
              group: official.group,
              functionaries: [official]
            })
          }
        }
      });

      if (functionariesWithoutGroup.functionaries.length > 0) {
        sortedByGroupArray.push(functionariesWithoutGroup);
      }

      return sortedByGroupArray;
    });
  }

  fetchClubTeams(id: string): Promise<AppTeam[]> {
    return this.http.get(this.getClubTeamsRoute(id), this.apiService.getJsonOptions())
      .toPromise()
      .then(response => this.resolveTeams(response))
      .catch(error => {
        return this.handleError(error);
      });
  }

  /***************************** RESOLVE FUNCTIONS *****************************/

  private resolveClub(jsonClub): Club {
    return new Club(jsonClub);
  }

  private resolveTeams(teamsJson): AppTeam[] {
    const teams: AppTeam[] = [];
    for (const k in teamsJson) {
      if (teamsJson.hasOwnProperty(k)) {
        const team = new AppTeam(teamsJson[k]);
        teams.push(team);
      }
    }
    return teams;
  }

  private resolveOfficials(json): Functionary[] {
    const functionaries: Functionary[] = [];
    for (let i = 0; i < json.length; i++) {
      functionaries.push(this.resolveFunctionary(json[i]) );
    }
    return functionaries;
  }

  private resolveFunctionary(json) {
    const functionary = new Functionary(json);

    for (let i = 0; i < json.pictures.length; i++) {
      const img = new BaseImage(json.pictures[i]);
      functionary.images[img.subtype] = img;
    }
    return functionary;
  }

  /***************************** ROUTE DEFINITIONS *****************************/

  private getClubRoute(id: string): string {
    return this.apiService.getBaseURL() + this.clubsRoute + id;
  }

  private getClubOfficialsRoute(id: string): string {
    return this.apiService.getBaseURL() + `officials/club/${id}`;
  }

  private getClubTeamsRoute(id: string): string {
    return this.apiService.getBaseURL() + this.clubsRoute + id + '/teams';
  }

  private handleError(error) {
    console.log(error);
    return null;
  }
}
